import { useCallback, useEffect, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  getSignerSignatureImage,
  patchUser,
  postSignerSignatureImage,
} from 'api';
import { FileUpload } from 'components/FileUpload';
import { useSnackContext } from 'hooks/useSnackContext';
import { UserProps } from 'types';

import fileUploadDefaultImage from './fileUploadDefaultImage.png';

export const SignerUserForm = (props: { user: UserProps }) => {
  const { user } = props;
  const { id, letter_name, letter_title, letter_phone, letter_email } = user;
  const [formData, setFormData] = useState({
    letter_name: letter_name,
    letter_title: letter_title,
    letter_email: letter_email,
    letter_phone: letter_phone,
  });

  const [error, setError] = useState({
    letterNameError: false,
    letterTitleError: false,
    letterEmailError: false,
    letterPhoneError: false,
  });
  const [downloadingSignature, setDownloadingSignature] = useState(false);
  const [editingImage, setEditingImage] = useState(false);
  const [posting, setPosting] = useState(false);
  const [signature, setSignature] = useState<null | string>(null);
  const { setSnack } = useSnackContext();
  useEffect(() => {
    const downloadSignature = async () => {
      try {
        setDownloadingSignature(true);
        const { data } = await getSignerSignatureImage();
        const fileReaderInstance = new FileReader();
        fileReaderInstance.readAsDataURL(data);
        fileReaderInstance.onload = () => {
          const base64data = fileReaderInstance.result as string;
          setSignature(base64data);
        };
      } catch (e) {
        console.error(e);
      } finally {
        setDownloadingSignature(false);
      }
    };
    downloadSignature();
  }, []);
  const handleSaveLetterData = useCallback(async () => {
    if (
      !formData.letter_name ||
      !formData.letter_email ||
      !formData.letter_phone ||
      !formData.letter_title
    ) {
      setError((prev) => ({
        ...prev,
        ...{
          letterNameError: !formData.letter_name,
          letterTitleError: !formData.letter_title,
          letterEmailError: !formData.letter_email,
          letterPhoneError: !formData.letter_phone,
        },
      }));
    } else {
      try {
        setPosting(true);
        const { data } = await patchUser(id, {
          data: formData,
        });
        // NOTE: the server should manage user roles and upgrade to pending (if it's not already)
        setFormData((prev) => ({
          ...prev,
          ...{
            letter_name: data.letter_name,
            letter_title: data.letter_title,
            letter_email: data.letter_email,
            letter_phone: data.letter_phone,
            first_name: data.first_name,
            last_name: data.last_name,
          },
        }));
        setSnack((props) => ({
          ...props,
          severity: 'success',
          open: true,
          message: 'Signer User Data Set',
        }));
      } catch (e) {
        console.log(e);
        setPosting(false);
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: 'Failed To Set Signer User Data',
          error: e as Error,
        }));
      }
      setPosting(false);
    }
  }, [id, setSnack, setFormData, formData]);

  const postImage = async (file: File) => {
    try {
      await postSignerSignatureImage(file);
      setSnack((props) => ({
        ...props,
        severity: 'success',
        open: true,
        message: 'Signature Data Set',
      }));

      const fileReaderInstance = new FileReader();
      fileReaderInstance.readAsDataURL(file);
      fileReaderInstance.onload = () => {
        const base64data = fileReaderInstance.result as string;
        setSignature(base64data);
      };
      setEditingImage(false);
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: 'Error Setting Signature Image',
      }));
      setEditingImage(false);
    }
  };
  return (
    <>
      <Typography variant='h4'>Signature Information</Typography>
      <Stack spacing={2}>
        <Typography variant='body1'>
          This information will be used in the signature block of the letters
          you sign within the Rabbit Portal. If you need to change the email you
          use to access the Rabbit Portal, please contact your Rabbit
          representative.
        </Typography>
        <TextField
          id='letter_name'
          variant='outlined'
          value={formData.letter_name}
          label='Full Name For Letter'
          onChange={(e) => {
            const { value } = e.currentTarget;
            if (error.letterNameError) {
              setError((prev) => {
                return { ...prev, letterNameError: !value };
              });
            }
            setFormData((prev) => {
              return { ...prev, letter_name: value };
            });
          }}
          error={error.letterNameError}
          helperText={error.letterNameError && 'Missing name for letter'}
        />
        <TextField
          id='letter_title'
          variant='outlined'
          value={formData.letter_title}
          label='Title'
          onChange={(e) => {
            const { value } = e.currentTarget;
            if (error.letterTitleError) {
              setError((prev) => {
                return { ...prev, letterTitleError: !value };
              });
            }
            setFormData((prev) => {
              return { ...prev, letter_title: value };
            });
          }}
          error={error.letterTitleError}
          helperText={error.letterTitleError && 'Missing title for letter'}
        />
        <TextField
          id='letter_email'
          variant='outlined'
          value={formData.letter_email}
          label='Email'
          onChange={(e) => {
            const { value } = e.currentTarget;
            if (error.letterEmailError) {
              setError((prev) => {
                return { ...prev, letterEmailError: !value };
              });
            }
            setFormData((prev) => {
              return { ...prev, letter_email: value };
            });
          }}
          error={error.letterEmailError}
          helperText={error.letterEmailError && 'Missing email for letter'}
        />
        <TextField
          id='letter_phone'
          variant='outlined'
          value={formData.letter_phone}
          label='Phone'
          onChange={(e) => {
            const { value } = e.currentTarget;
            if (error.letterPhoneError) {
              setError((prev) => {
                return { ...prev, letterPhoneError: !value };
              });
            }
            setFormData((prev) => {
              return { ...prev, letter_phone: value };
            });
          }}
          error={error.letterPhoneError}
          helperText={
            error.letterPhoneError && 'Missing phone number for letter'
          }
        />
      </Stack>
      <Divider sx={{ width: '100%' }} />
      <Typography sx={{ mt: 2, mb: 2 }} variant='h5'>
        Signature Image
      </Typography>
      {/* {!downloadingSignature ? (
        <Box sx={{ mt: 2, mb: 3, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ width: '30%' }}>
            <img
              alt='signature'
              src={signature || fileUploadDefaultImage}
              style={{ height: '200px' }}
            />
          </Box>
        </Box>
      ) : (
        <Skeleton sx={{ height: 200, width: 200 }} />
      )} */}
      {signature && !editingImage ? (
        <>
          <Box sx={{ mt: 2, mb: 3, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ width: '30%' }}>
              <img
                alt='signature'
                src={signature || fileUploadDefaultImage}
                style={{ height: '200px' }}
              />
            </Box>
          </Box>
          <Divider />
          <Button
            sx={{ float: 'right' }}
            onClick={() => setEditingImage(true)}
            variant='text'
          >
            REPLACE
          </Button>
        </>
      ) : downloadingSignature ? (
        <Skeleton sx={{ height: 200, width: 200 }} />
      ) : (
        <>
          <FileUpload
            hoverLabel='Click or drag to upload signature image'
            accept='image/png'
            width='100%'
            height='180px'
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (
                event.target.files !== null &&
                event.target?.files?.length > 0
              ) {
                postImage(event.target.files[0]);
              }
            }}
            onDrop={(event: React.DragEvent<HTMLElement>) => {
              postImage(event.dataTransfer.files[0]);
            }}
          />
          {signature && (
            <>
              <Divider />
              <Button
                sx={{ float: 'right' }}
                onClick={() => setEditingImage(false)}
                variant='text'
              >
                CANCEL
              </Button>
            </>
          )}
        </>
      )}

      <LoadingButton
        sx={{ mt: 2 }}
        // disabled={disabled}
        loading={posting}
        variant='contained'
        onClick={handleSaveLetterData}
      >
        SET SIGNER DATA
      </LoadingButton>
    </>
  );
};
